<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4 mb-5">
    <div style="max-width:780px">
      <button class="mdm-back" @click="$router.push('/mdm')">
        <font-awesome-icon :icon="['fas', 'arrow-left']" /> CAGISTA MDM
      </button>
      <h3 class="mb-1 mt-1">AMAPI Config</h3>
      <div style="font-style: italic; color:var(--bo-muted)">
        Android Management API-tilkobling. Lagres i Couchbase (ikke i kildekode/git); tjenestekonto-JSON
        lagres <strong>kryptert</strong>. App-distribusjon (self-hosted APK) styres på
        <router-link to="/mdmApps">CAGISTA MDM → Apper</router-link>.
      </div>

      <div v-if="isLocalDev()" class="mdm-devbanner mt-2">
        <font-awesome-icon :icon="['fas', 'lock']" />
        <span>
          Kjører fra <code>localhost</code>. Denne siden er <strong>ikke</strong> skrivebeskyttet — den
          konfigurerer bare dette miljøet, og lagring her rører ingenting i produksjon. Men det er her
          delingen oppstår: peker <code>enterpriseId</code> på produksjonens enterprise, jobber
          <router-link to="/mdm">Enheter</router-link>, <router-link to="/mdmPolicies">Policyer</router-link>,
          <router-link to="/mdmApps">Apper</router-link> og
          <router-link to="/mdmEnroll">Enroll</router-link> mot den ekte flåten. Derfor er de sidene
          skrivebeskyttet herfra.
        </span>
      </div>

      <div class="cfg-card">
        <div class="cfg-card-header">
          <span class="cfg-icon"><font-awesome-icon :icon="['fas', 'plug']" /></span>
          <h5>AMAPI-tilkobling</h5>
          <span class="badge" :class="config.credentialsConfigured ? 'bg-success' : 'bg-secondary'">
            {{ config.credentialsConfigured ? 'Konfigurert ✓' : 'Ikke satt' }}
          </span>
        </div>
        <div class="cfg-desc">
          Tjenestekontoen og prosjektet som styrer alle enhetene.
        </div>

        <div class="alert alert-info mt-3 py-2" style="font-size:0.85rem">
          <font-awesome-icon :icon="['fas', 'lock']" />
          Tjenestekonto-JSON vises aldri her igjen etter lagring. La feltet stå <strong>tomt</strong> for å
          beholde eksisterende nøkkel — fyll det kun ut for å erstatte den.
        </div>

        <config-lock ref="lock" :configured="isConfigured">
        <div class="mt-2">
          <label class="form-label mb-1 mdm-label">Tjenestekonto-JSON (service account key)</label>
          <textarea class="form-control" rows="5" v-model="form.credentialsJson"
                    placeholder="Lim inn ny service-account JSON for å erstatte. Tom = behold eksisterende."
                    style="font-family:monospace; font-size:0.8rem"></textarea>
          <span style="color:var(--bo-muted); font-size:0.78rem">kilde: {{ sourceLabel(config.credentialsSource) }}</span>
        </div>

        <div class="row g-3 mt-1">
          <div class="col-md-5">
            <label class="form-label mb-1 mdm-label">Enterprise ID</label>
            <input class="form-control" type="text" v-model="form.enterpriseId" placeholder="LC01a4gxdu" />
            <span style="color:var(--bo-muted); font-size:0.78rem">kilde: {{ sourceLabel(config.enterpriseIdSource) }}</span>
          </div>
          <div class="col-md-4">
            <label class="form-label mb-1 mdm-label">Project ID</label>
            <input class="form-control" type="text" v-model="form.projectId" placeholder="cagista-amapi" />
            <span style="color:var(--bo-muted); font-size:0.78rem">kilde: {{ sourceLabel(config.projectIdSource) }}</span>
          </div>
          <div class="col-md-3">
            <label class="form-label mb-1 mdm-label">Enhetskvote</label>
            <input class="form-control" type="number" min="1" step="1" v-model.number="form.deviceQuota" placeholder="500" />
          </div>
        </div>
        <div style="color:var(--bo-muted); font-size:0.78rem" class="mt-1">
          Enhetskvote = antallet Google har innvilget for prosjektet. Ingen Google-API leser dette — hold det i
          synk manuelt; vises som «Enhetskvote» på MDM-siden.
        </div>

        <div class="mt-3 d-flex align-items-center gap-3">
          <button class="bo_btn" @click="save" :disabled="saving">{{ saving ? 'Lagrer…' : 'Lagre' }}</button>
          <span v-if="message" :class="error ? 'text-danger' : 'text-success'">{{ message }}</span>
        </div>
        </config-lock>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfigLock from "@/components/other/ConfigLock";
import MdmService from "@/services/system/MdmService";
import { isLocalDev } from "@/util/MdmLocalGuard";

export default {
  name: "AmapiConfig",
  components: { LoadingSpinner, ConfigLock },
  computed: {
    // Already configured (→ starts locked): AMAPI service-account credentials are stored.
    isConfigured() { return !!this.config.credentialsConfigured; }
  },
  data() {
    return {
      loading: true,
      config: {},
      form: { credentialsJson: "", enterpriseId: "", projectId: "", deviceQuota: 500 },
      saving: false,
      message: "",
      error: false
    };
  },
  mounted() {
    this.loadConfig();
  },
  methods: {
    isLocalDev,
    sourceLabel(source) {
      return source === "couchbase" ? "Couchbase" : "—";
    },
    loadConfig() {
      MdmService.getConfig().then(
        (response) => {
          this.config = response.data || {};
          // Prefill the non-secret ids; never the credentials (kept blank = keep existing).
          this.form.enterpriseId = this.config.enterpriseId || "";
          this.form.projectId = this.config.projectId || "";
          this.form.deviceQuota = this.config.deviceQuota || 500;
          this.loading = false;
        },
        (error) => {
          console.log(error);
          this.loading = false;
          this.error = true;
          this.message = this.errMsg(error, "Kunne ikke hente konfigurasjon.");
        }
      );
    },
    save() {
      this.saving = true;
      this.message = "";
      this.error = false;

      // The key is pasted as formatted JSON; minify it to one line on save (empty = keep existing).
      let credentialsJson = this.form.credentialsJson.trim();
      if (credentialsJson) {
        try {
          credentialsJson = JSON.stringify(JSON.parse(credentialsJson));
        } catch (e) {
          this.saving = false;
          this.error = true;
          this.message = "Ugyldig JSON i tjenestekonto-nøkkelen.";
          return;
        }
      }

      MdmService.saveConfig({
        credentialsJson: credentialsJson,
        enterpriseId: this.form.enterpriseId,
        projectId: this.form.projectId,
        deviceQuota: this.form.deviceQuota
      }).then(
        (response) => {
          this.saving = false;
          this.config = response.data || {};
          this.form.credentialsJson = "";   // don't keep the secret in the DOM
          this.error = false;
          this.message = "Lagret ✓";
          this.$refs.lock.relock();   // re-lock after a successful save
        },
        (error) => {
          this.saving = false;
          this.error = true;
          this.message = this.errMsg(error, "Kunne ikke lagre konfigurasjon.");
        }
      );
    },
    errMsg(error, fallback) {
      const data = error && error.response ? error.response.data : null;
      return (data && data.error) || fallback;
    }
  }
};
</script>

<style scoped>
/* Localhost notice, same shape as the other MDM pages (informational here, not a lock) */
.mdm-devbanner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 780px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.84rem;
  font-style: normal;
  color: var(--bo-brand-ink);
  background: var(--bo-brand-tint);
  border: 1px solid #ffd591;
}

.mdm-back {
  border: none;
  background: none;
  color: var(--bo-brand-ink);
  padding: 0;
  font-size: 0.9rem;
  cursor: pointer;
}
.mdm-back:hover {
  text-decoration: underline;
}
.mdm-label {
  font-size: 0.85rem;
  color: var(--bo-muted);
}
.cfg-card {
  border: 1px solid #e6e6e6;
  border-left: 3px solid #ECB109;
  border-radius: 10px;
  background: var(--bo-surface);
  padding: 1rem 1.25rem 1.25rem;
  margin-top: 1.25rem;
}
.cfg-card-header {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}
.cfg-card-header .cfg-icon {
  color: #ECB109;
  font-size: 1.05rem;
  width: 1.3rem;
  text-align: center;
}
.cfg-card-header h5 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
}
.cfg-card-header .badge {
  margin-left: auto;
  font-weight: 500;
}
.cfg-desc {
  font-style: italic;
  color: var(--bo-muted);
  font-size: 0.85rem;
  margin-top: 0.4rem;
}
</style>
